import React, { ReactElement, useMemo } from 'react';
import { parseISO } from 'date-fns';
import {
  useListReports,
  useListSprints,
  useListProducts,
  useRemoveReport,
  UserEntity,
  ProductStatus,
  ReportEntity,
  useCreateReport,
  useUpdateReport,
  useFinishedDays,
  useSetDayFinished,
} from '@data';
import { useToggleState, fullDate, sprintPageRange, sprintYearPages } from '@app/utils';
import { useOrganizationId, useReportingWindow } from '@app/utils/hooks';
import { useOrganizations } from '@data/hooks/organizations';
import { Roles, SprintReportTab } from '@app/utils/types';
import { FULL_WORKDAY_MINUTES } from '@app/constants/app';
import { onlyProducts } from '@data/utils/transform-user-reports-data';
import { Pagination } from '@app/components/core';
import PhillReportDialog from '@app/components/shared/phillReport/PhillReportDialog';
import EditEntryDialog from '@app/components/shared/phillReport/EditEntryDialog';
import RemoveReportModal from '@app/components/shared/removeReportModal';
import ReportIncidentDialog from '@app/components/shared/phillReport/ReportIncidentDialog';
import { Loader } from '@ui';
import PendingAbsencesProvider from '@app/components/shared/sprintDayReport/PendingAbsencesProvider';
import SprintHeader from './SprintHeader';
import SprintOverview from './SprintOverview';
import ToReportList from './ToReportList';
import { productsTakenOnDay } from './productsTakenOnDay';

export interface SprintReportingProps {
  user: UserEntity;
}

function SprintReportingLoaded({
  user,
  organizationId,
}: SprintReportingProps & { organizationId: string }): ReactElement {
  const [showWeekend, toggleShowWeekend] = useToggleState(false);
  const params = new URLSearchParams(window.location.search);
  const [activeTab, setActiveTab] = React.useState<SprintReportTab>(
    params.get('tab') === SprintReportTab.completed ? SprintReportTab.completed : SprintReportTab.toReport,
  );
  const [productIds, setProductIds] = React.useState<string[]>([]);
  const isToReport = activeTab === SprintReportTab.toReport;

  // The date range applies only once both ends are set; with one set, the empty one is flagged as required
  // and the unfiltered view stays on screen.
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const rangeActive = !isToReport && Boolean(from && to);
  const fromMissing = Boolean(to) && !from;
  const toMissing = Boolean(from) && !to;

  const { data: products } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  const todayDate = useMemo(() => new Date(), []);

  // The signed-in person's role in this organization, from the same source `withAuth` gates routes on.
  const { data: organizations } = useOrganizations(user.id);
  const role = organizations?.items?.find(org => org.id === organizationId)?.role;
  const canSeeMembers = role === Roles.admin || role === Roles.productManager;

  // Tab, filter or year change starts at the top of the list.
  const [page, setPage] = React.useState(0);
  React.useEffect(() => {
    setPage(0);
  }, [activeTab, from, to, productIds]);

  // Overview's sprint universe: everything that has started, or the filter window once both bounds are set.
  const sprintsRange = React.useMemo(
    () =>
      rangeActive
        ? { from: new Date(from), to: new Date(`${to}T23:59:59.999`), limit: 500 }
        : { to: todayDate, limit: 500 },
    [rangeActive, from, to, todayDate],
  );
  const { data: sprints } = useListSprints(sprintsRange);

  const yearPages = React.useMemo(() => sprintYearPages(sprints?.items ?? []), [sprints]);
  const pageCount = Math.max(1, yearPages.length);
  const currentPage = Math.min(page, pageCount - 1);
  const pageSprints = React.useMemo(() => yearPages[currentPage]?.sprints ?? [], [yearPages, currentPage]);
  // The page asks for exactly its own sprints — first start to last end — so a sprint running over New Year
  // arrives whole rather than half-reported on the year it began in.
  const pageRange = React.useMemo(() => sprintPageRange(pageSprints), [pageSprints]);

  // To report covers this sprint and the last one; Overview covers the year on show. Both ask for every
  // product: whether a day is full depends on the whole day, so the product filter is applied below instead.
  const reportingWindow = useReportingWindow();
  const paramsForReports = React.useMemo(
    () =>
      isToReport
        ? { from: reportingWindow?.from, to: reportingWindow?.to, productIds: [] }
        : { from: pageRange?.from, to: pageRange?.to, productIds: [] },
    [isToReport, reportingWindow, pageRange],
  );

  // Both tabs manage weekends themselves, so the whole week is always fetched. Overview waits for a year to
  // ask about rather than firing off an unbounded query.
  const { data: reports } = useListReports(
    organizationId,
    user?.id as string,
    paramsForReports,
    isToReport ? !!reportingWindow : !!pageRange,
    false,
  );

  const workDays = React.useMemo(
    () => (reports?.pages ?? []).flatMap(reportPage => reportPage.workDaysData),
    [reports],
  );
  const fullDays = React.useMemo(
    () => workDays.filter(day => day.totalMinutes >= FULL_WORKDAY_MINUTES).map(day => day.date),
    [workDays],
  );

  /*
   * The sprints and the days they group have to reach the view together. `pageSprints` flips the moment a
   * year is clicked, while its days are still in flight, and a sprint with no matching days renders as
   * nothing — so the list would blank out and then repopulate. Recomputing only when the days change holds
   * the previous year on screen until its replacement is ready, and gives the fade something to animate.
   */
  const shown = React.useMemo(
    () => ({ key: pageSprints[0]?.id ?? currentPage, sprints: pageSprints, workDays, fullDays }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workDays],
  );
  // Filtered after `shown`, so a product change never pairs the sprints of a year still loading with these days.
  const shownWorkDays = React.useMemo(() => onlyProducts(shown.workDays, productIds), [shown, productIds]);

  const { mutate: removeReport } = useRemoveReport(organizationId!, user?.id as string, paramsForReports, false);

  const [isAddFormOpen, toggleAddForm] = useToggleState(false);
  const [isDeleteFormOpen, toggleDeleteForm] = useToggleState(false);
  const [selectedReport, setReportSelected] = React.useState<ReportEntity | null>(null);
  const [reportToEdit, setReportToEdit] = React.useState<ReportEntity | null>(null);
  // Kept after closing, so the dialog does not change day while it fades out.
  const [workDayToAdd, setWorkDayToAdd] = React.useState('');
  const dayToAdd = useMemo(() => (workDayToAdd ? parseISO(workDayToAdd) : todayDate), [workDayToAdd, todayDate]);
  // Read off the live list rather than the click, so hours saved from the dialog's Incident tab count at once.
  const reportedOnDayToAdd = useMemo(() => {
    const reports = workDays.find(day => day.date === workDayToAdd)?.reports ?? [];

    return {
      minutes: reports.reduce((sum, report) => sum + report.minutes, 0),
      productIds: reports.filter(report => !report.incidentId).map(report => report.involvement.product.id),
    };
  }, [workDays, workDayToAdd]);

  // Moving an entry onto a product that already has one that day would split the day's line in two.
  const otherProductIdsOnEditedDay = useMemo(
    () =>
      reportToEdit?.workday ? productsTakenOnDay(workDays, fullDate(reportToEdit.workday), reportToEdit) : undefined,
    [reportToEdit, workDays],
  );

  const showEditForm = report => {
    setReportToEdit(report);
  };
  const closeEditForm = () => {
    setReportToEdit(null);
  };

  const showAddForm = (workDay: string) => {
    setWorkDayToAdd(workDay);
    toggleAddForm();
  };

  const showDeleteForm = report => {
    setReportSelected(report);
    toggleDeleteForm();
  };
  const closeDeleteForm = () => {
    toggleDeleteForm();
    setReportSelected(null);
  };

  const handleDeleting = () => {
    if (selectedReport) {
      removeReport(selectedReport?.id);
    }
    closeDeleteForm();
  };

  const { mutateAsync: createReportAsync } = useCreateReport(
    user?.id as string,
    organizationId!,
    paramsForReports,
    false,
  );

  const { mutate: updateReport, mutateAsync: updateReportAsync } = useUpdateReport(
    organizationId!,
    user?.id as string,
    paramsForReports,
    false,
  );

  // Asked for the window on show, so Overview knows a day two sprints back was settled and does not mark it
  // as still owed. Taking the mark off stays bounded to the To report window, which `undoableFrom` carries.
  const finishedWindow = isToReport ? reportingWindow : pageRange;
  const { data: finished, isError: isFinishedError } = useFinishedDays(
    organizationId,
    user?.id,
    finishedWindow,
    !!finishedWindow,
  );
  const { mutate: setDayFinished } = useSetDayFinished(organizationId, user?.id);
  const handleSetFinished = React.useCallback(
    (workday: string, isFinished: boolean) => setDayFinished({ workday, finished: isFinished }),
    [setDayFinished],
  );

  const handleUpdateReport = (values: Partial<ReportEntity>) => {
    updateReport(values);
    closeEditForm();
  };

  const tabContent = isToReport ? (
    <ToReportList
      workDaysData={workDays}
      todayDate={todayDate}
      windowStart={reportingWindow && fullDate(reportingWindow.from)}
      showAddForm={showAddForm}
      showEditForm={showEditForm}
      showDeleteForm={showDeleteForm}
      showWeekend={showWeekend}
      toggleShowWeekend={toggleShowWeekend}
      finishedDays={finished?.days ?? []}
      onSetFinished={handleSetFinished}
    />
  ) : (
    // Keyed and faded on `shown`, which only advances once the rows for a year have arrived. Keying on the
    // year instead would start the fade on the click, while the list is still empty.
    <SprintOverview
      key={shown.key}
      className="duration-200 animate-in fade-in-0"
      workDaysData={shownWorkDays}
      todayDate={todayDate}
      showAddForm={showAddForm}
      showEditForm={showEditForm}
      showDeleteForm={showDeleteForm}
      sprints={shown.sprints}
      fullDays={shown.fullDays}
      finishedDays={finished?.days ?? []}
      undoableFrom={reportingWindow && fullDate(reportingWindow.from)}
      onSetFinished={handleSetFinished}
      pager={
        <Pagination
          className="mt-auto pt-8"
          page={currentPage}
          pageCount={pageCount}
          onChange={setPage}
          labels={yearPages.map(yearPage => String(yearPage.year))}
          reversed
        />
      }
    />
  );

  return (
    // Fills the page frame so a short year can push its pager to the bottom rather than leave it mid-page.
    <div className="flex w-full flex-1 flex-col">
      <SprintHeader
        organizationId={organizationId}
        canSeeMembers={canSeeMembers}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        productIds={productIds}
        setProductIds={setProductIds}
        products={products}
        from={from}
        to={to}
        setFrom={setFrom}
        setTo={setTo}
        fromMissing={fromMissing}
        toMissing={toMissing}
      />

      {/* Waits for the finished marks too: without them a day marked finished shows as owed until they land. */}
      {reports?.pages && (finished || isFinishedError) ? (
        tabContent
      ) : (
        <div className="w-full">
          {[...new Array(10)].map((x, i) => (
            <div key={Number(i)} className="not-last:mb-5 overflow-hidden rounded-sm bg-card">
              <div className="h-10 border-b border-border bg-background" />
            </div>
          ))}
        </div>
      )}

      <EditEntryDialog
        report={reportToEdit && !reportToEdit.incidentId ? reportToEdit : null}
        onHide={closeEditForm}
        organizationId={organizationId}
        userId={user?.id}
        onSubmit={handleUpdateReport}
        takenProductIds={otherProductIdsOnEditedDay}
      />

      {/* Incident hours are corrected in the form they were reported in: a day and two clock times,
          not a number of hours. The same write either way, so one ledger. */}
      <ReportIncidentDialog
        show={!!reportToEdit?.incidentId}
        onHide={closeEditForm}
        userId={user?.id}
        report={reportToEdit?.incidentId ? reportToEdit : null}
        onSubmit={values => updateReportAsync(values)}
      />

      <PhillReportDialog
        show={isAddFormOpen}
        onHide={toggleAddForm}
        organizationId={organizationId}
        userId={user?.id as string}
        onSubmit={createReportAsync}
        date={dayToAdd}
        reportedMinutes={reportedOnDayToAdd.minutes}
        reportedProductIds={reportedOnDayToAdd.productIds}
      />

      <RemoveReportModal show={isDeleteFormOpen} onHide={closeDeleteForm} onRemove={handleDeleting} />
    </div>
  );
}

const SprintReporting = ({ user }: SprintReportingProps): ReactElement => {
  const organizationId = useOrganizationId();

  if (!organizationId) {
    return <Loader />;
  }

  return (
    <PendingAbsencesProvider organizationId={organizationId} userId={user?.id}>
      <SprintReportingLoaded user={user} organizationId={organizationId} />
    </PendingAbsencesProvider>
  );
};

export default SprintReporting;
